import { 
    useTheme, 
    Box, 
    Typography, 
    Paper, 
    Chip, 
    Divider 
} from '@mui/material';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';


const ArrList = ({ arrList }) => {
    if (!arrList || Object.keys(arrList).length === 0) return null;
    const text = Object.entries(arrList)
        .sort((a, b) => b[1] - a[1])
        .map(([k, v]) => `${k}:${v}`)
        .join(' · ');
    return (
        <Typography 
            variant="caption" 
            color="text.primary" 
            display="block" 
            mt={0.75}
        >
            {text}
        </Typography>
    );
};

const CrewLine = ({ member, canView, onViewClick }) => {
    const theme = useTheme();
    if (!member) return null;
    return (
        <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            sx={{
                color: member.gender === 'FM' ? theme.palette.secondary.main : theme.palette.primary.dark,
                cursor: canView ? 'pointer' : 'default',
                '&:hover': canView ? { textDecoration: 'underline' } : {},
            }}
            onClick={() => { if (canView) onViewClick(member.crewCode, member.crewName); }}
        >
            <Typography 
                variant="body2" 
                fontWeight="bold" 
                sx={{ whiteSpace: 'nowrap', minWidth: 36 }}
            >
                {member.crewCode}
            </Typography>
            <Typography 
                variant="body2" 
                sx={{ flex: 1, minWidth: 0 }}
            >
                {member.crewName}
            </Typography>
            <Typography 
                variant="body2" 
                sx={{ whiteSpace: 'nowrap', opacity: 0.75 }}
            >
                {member.post} | {member.base}{member.trnDuty ? ` | ${member.trnDuty}` : ''}
            </Typography>
        </Box>
    );
};

const BuddyCard = ({ row, index, canView, onViewClick }) => {
    const theme = useTheme();
    return (
        <Paper elevation={1} sx={{ mb: 1, p: 1.5, borderLeft: `3px solid ${theme.palette.primary.main}`, borderRadius: 1 }}>
            <FlexBetween mb={0.75}>
                <Typography 
                    variant="body2" 
                    color="text.secondary"
                >
                    #{index + 1}
                </Typography>
                
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        Count: 
                    </Typography>
                    <Typography 
                        variant="body2" 
                        color="primary" 
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        {row.count}
                    </Typography>
                </Box>
            </FlexBetween>
            <CrewLine 
                member={row.crewA} 
                canView={canView} 
                onViewClick={onViewClick} 
            />
            <Divider sx={{ my: 1 }} />
            <CrewLine 
                member={row.crewB} 
                canView={canView} 
                onViewClick={onViewClick} 
            />
            <ArrList arrList={row.arrList} />
        </Paper>
    );
};

const IndividualCard = ({ row, index, canView, onViewClick }) => {
    const theme = useTheme();
    return (
        <Paper 
            elevation={1} 
            sx={{ 
                p: 1.5, 
                borderLeft: `2px solid ${theme.palette.primary.main}`, 
                borderRadius: 1 
            }}
        >
            <Box display="flex" alignItems="center" gap={1}>
                <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    #{index + 1}
                </Typography>
                <Box
                    flex={1}
                    minWidth={0}
                    sx={{
                        color: row.gender === 'FM' ? theme.palette.secondary.main : theme.palette.primary.dark,
                        cursor: canView ? 'pointer' : 'default',
                        '&:hover': canView ? { textDecoration: 'underline' } : {},
                    }}
                    onClick={() => { if (canView) onViewClick(row.crewCode, row.crewName); }}
                >
                    <Typography 
                        variant="subtitle2" 
                        noWrap
                    >
                        {row.crewCode} - {row.crewName}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        Arrivals: 
                    </Typography>
                    <Typography 
                        variant="body2" 
                        color="primary" 
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        {row.count}
                    </Typography>
                </Box>
            </Box>

            <Box 
                display="flex" 
                flexWrap="wrap"
                alignItems="center" 
                gap={1} 
                mt={0.75} 
            >
                {row.post && (
                    <Chip 
                        label={row.post} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                        sx={{ height: 20, fontSize: '0.7rem' }} 
                    />
                )}
                {row.base && (
                    <Chip 
                        label={row.base} 
                        size="small" 
                        variant="outlined" 
                        sx={{ height: 20, fontSize: '0.7rem' }} 
                    />
                )}
                {row.quals && (
                    <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        noWrap display="block" 
                        mt={0.5}
                    >
                        {row.quals}
                    </Typography>
                )}
            </Box>

            <ArrList arrList={row.arrList} />
        </Paper>
    );
};


const TopBuddyList = ({ rows, view, canView, onViewClick }) => {
    if (!rows?.length) {
        return (
            <FlexCenter height={LAYOUT_HEIGHT}>
                <Typography color="error">No data available</Typography>
            </FlexCenter>
        );
    }
    

    return (
        <Box
            display="flex"
            flexDirection="column"
            width="100%"
            gap={2}
        >
            {rows.map((row, index) =>
                view === 'buddy' ? (
                    <BuddyCard 
                        key={row._id} 
                        row={row} 
                        index={index} 
                        canView={canView} 
                        onViewClick={onViewClick} 
                    />
                ) : (
                    <IndividualCard 
                        key={row._id} 
                        row={row} 
                        index={index} 
                        canView={canView} 
                        onViewClick={onViewClick} 
                    />
                )
            )}
        </Box>
    );
};

export default TopBuddyList;
